import { useEffect, useState } from 'react';

export type ConnectionBannerStatus = 'offline' | 'reconnected';

export const CONNECTION_BANNER_MESSAGES: Record<ConnectionBannerStatus, string> = {
  offline: 'Connection lost. You can save changes once reconnected.',
  reconnected: 'Re-connected',
};

export const RECONNECTED_BANNER_MS = 2000;

/**
 * Banner status for a connection signal: `offline` while disconnected, then `reconnected` for
 * {@link RECONNECTED_BANNER_MS}. A `null` signal (unknown) keeps the current status.
 */
export function useConnectionBannerStatus(isOffline: boolean | null): ConnectionBannerStatus | null {
  const [status, setStatus] = useState<ConnectionBannerStatus | null>(isOffline ? 'offline' : null);
  const [lastKnown, setLastKnown] = useState(isOffline);

  // Adjusting state during render avoids an intermediate render with no status on reconnect.
  if (isOffline !== null && isOffline !== lastKnown) {
    setLastKnown(isOffline);
    setStatus(isOffline ? 'offline' : lastKnown ? 'reconnected' : status);
  }

  useEffect(() => {
    if (status !== 'reconnected') return undefined;
    const timeout = setTimeout(() => setStatus(null), RECONNECTED_BANNER_MS);
    return () => clearTimeout(timeout);
  }, [status]);

  return status;
}
